import axios from "axios";

export const BASE_URL = "https://api.okgini.com/api";

export const getHelper = async (url, headers) => {
  return axios
    .get(url, {
      headers,
    })
    .then((response) => response.data)
    .catch((error) => error);
};

export const postHelper = async (url, body, headers) => {
  return axios
    .post(url, body, {
      headers: { ...headers, "Content-Type": "application/json" },
    })
    .then((response) => response.data)
    .catch((error) => error);
};

export const deleteHelper = async (url, headers) => {
  try {
    const response = await axios.delete(url, { headers });
    return response.data;
  } catch (error) {
    return error;
  }
};

export const getQueryParam = (param) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(param);
};

export const getToken = () => {
  const typeParam = getQueryParam("type");
  const lidParam = getQueryParam("lid");

  if (typeParam) {
    return typeParam;
  }

  const localStorageToken = localStorage.getItem(`token-${lidParam}`);
  if (localStorageToken) {
    return localStorageToken;
  }

  return `${lidParam}-en`;
};

/** Loyalty token from storage only — no guest `{lid}-en` fallback. Use after 2FA login/booking. */
export const getStoredLoyaltyToken = (lid) =>
  lid ? localStorage.getItem(`token-${lid}`) : null;

/** Write loyalty token and confirm it is readable before continuing. */
export const persistLoyaltyToken = (lid, token) => {
  if (!lid || !token) return null;
  const key = `token-${lid}`;
  localStorage.setItem(key, token);
  const stored = localStorage.getItem(key);
  return stored === token ? stored : null;
};

export const validateGuestAndLid = (lid, type) => {
  return lid && lid.length === 3 && type.substring(0, 3) === lid;
};

export const setLocalStorageWithTimer = (key, value, expirationTime) => {
  const item = {
    value,
    expirationTime: Date.now() + expirationTime,
  };

  localStorage.setItem(key, JSON.stringify(item));

  setTimeout(() => {
    localStorage.removeItem(key);
  }, expirationTime);
};

export const getLocalStorageWithTimer = (key) => {
  const item = localStorage.getItem(key);

  if (item) {
    const { value, expirationTime } = JSON.parse(item);

    if (expirationTime > Date.now()) {
      return value;
    } else {
      localStorage.removeItem(key);
    }
  }

  return null;
};

export const removeAccents = (str) =>
  str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export const parseSafeDate = (dateString) => {
  if (!dateString) return null;

  // Try native parsing first
  let parsedDate = Date.parse(dateString);
  if (!isNaN(parsedDate)) {
    return new Date(parsedDate);
  }

  // Try to manually convert to ISO 8601 format
  const isoDate = dateString
    .replace(" ", "T") // Replace space with 'T'
    .replace(/(\d{2}:\d{2}:\d{2})$/, "$1Z"); // Add 'Z' at the end if missing

  parsedDate = Date.parse(isoDate);
  if (!isNaN(parsedDate)) {
    return new Date(parsedDate);
  }

  console.warn(`Invalid date format: ${dateString}`);
  return null; // Return null if parsing fails
};
export const formatMoney = (amount, general) => {
  if (amount == null) return "";

  const symbol = general?.values?.currencySymbol ?? "€";
  const rightSide = general?.values?.displayCurrencySymbolOnRightSide ?? true;

  const value = Number(amount).toFixed(2);

  return rightSide ? `${value} ${symbol}` : `${symbol}${value}`;
};

export const toNumberOrNull = (v) => {
  if (v === undefined || v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const sendingCode = (phone, accommodationId) =>
  getHelper(
    `${BASE_URL}/routee/sendCodeToCustomer/mobileNumber/${phone}/accommodationId/${accommodationId}`,
    {
      authorization: `token ${accommodationId}-en`,
    },
  );

export const verifyCode = (accommodationId, body) =>
  postHelper(`${BASE_URL}/routee/verifyCode`, body, {
    authorization: `token ${accommodationId}-en`,
    "content-type": "application/json",
  });

export const createAccount = (accommodationId, body) =>
  postHelper(`${BASE_URL}/loyaltyClub/acm/${accommodationId}`, body, {
    "content-type": "application/json",
  });

export const checkUserIfExistAndGetToken = async ({
  mobile,
  accommodationId,
}) => {
  const response = await axios.get(
    `${BASE_URL}/routee/checkIfExistsAndLogIn/mobileNumber/${mobile}/accommodationId/${accommodationId}`,
    {
      headers: { Authorization: `lid ${accommodationId}` },
    },
  );
  return response.data;
};

export const fetchLoyaltyMemberWithToken = async ({
  token,
  accommodationId,
}) => {
  const response = await axios.get(
    `${BASE_URL}/routee/getLoyaltyMember/token/${token}`,
    {
      headers: { Authorization: `lid ${accommodationId}` },
    },
  );
  return response.data;
};

export const checkIfTokenHasExpired = async ({ token, accommodationId }) => {
  const response = await axios.get(
    `${BASE_URL}/routee/checkIfTokenHasExpired/token/${token}`,
    {
      headers: { Authorization: `lid ${accommodationId}` },
    },
  );
  return response.data;
};

export const loadFonts = async (fontIds) => {
  const fontNames = {}; // Object to store font names

  // Load each font and assign names
  const fontPromises = fontIds.map(async (id, index) => {
    if (!id) return; // header-only setups store fonts as [null, headerId]
    const fontName = `CustomFont${index}`;
    const fontUrl = `${BASE_URL}/attachments/${id}`;

    const newStyle = document.createElement("style");
    newStyle.appendChild(
      document.createTextNode(`
      @font-face {
        font-family: '${fontName}';
        src: url('${fontUrl}') format('opentype');
      }
    `),
    );
    document.head.appendChild(newStyle);

    if ("fonts" in document) {
      await document.fonts.load(`1em ${fontName}`);
    }
    fontNames[index === 0 ? "body" : "header"] = fontName;
  });

  await Promise.all(fontPromises);
  return fontNames; // Return the object with font names
};

/**
 * Guest "activity day" can start at a configured time (e.g. 07:00) instead of midnight.
 * All calculations use the browser's local timezone to match existing moment() usage.
 */

const TIME_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

/**
 * @param {unknown} value - e.g. from general.values.guestActivityDayStartTime
 * @returns {{ hours: number, minutes: number, totalMinutes: number } | null}
 */
export function parseGuestActivityDayStartTime(value) {
  if (value === null || value === undefined) return null;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const match = trimmed.match(TIME_RE);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  const totalMinutes = hours * 60 + minutes;
  return { hours, minutes, totalMinutes };
}

/**
 * "00:00" or missing/invalid means keep legacy calendar-day behavior.
 */
export function isCustomGuestActivityDayShift(parsed) {
  return parsed != null && parsed.totalMinutes > 0;
}

export function getGuestActivityDayStartMinutesFromGeneral(general) {
  const parsed = parseGuestActivityDayStartTime(
    general?.values?.guestActivityDayStartTime,
  );
  return isCustomGuestActivityDayShift(parsed) ? parsed.totalMinutes : null;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

export function formatLocalYMD(date) {
  const y = date.getFullYear();
  const m = pad2(date.getMonth() + 1);
  const d = pad2(date.getDate());
  return `${y}-${m}-${d}`;
}

function parseLocalYMD(ymd) {
  const [y, mo, da] = ymd.split("-").map((x) => Number(x));
  if (!y || !mo || !da) return null;
  return new Date(y, mo - 1, da);
}

export function addDaysToYMD(ymd, deltaDays) {
  const d = parseLocalYMD(ymd);
  if (!d) return ymd;
  d.setDate(d.getDate() + deltaDays);
  return formatLocalYMD(d);
}

function localBoundaryAt(ymd, totalMinutes) {
  const d = parseLocalYMD(ymd);
  if (!d) return null;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  d.setHours(h, m, 0, 0);
  return d;
}

/**
 * Calendar date (YYYY-MM-DD) that labels the logical activity day containing `referenceDate`.
 * Without custom shift, this is the calendar day of `referenceDate`.
 */
export function getLogicalActivityDayAnchorDate(referenceDate, startMinutes) {
  const ymd = formatLocalYMD(referenceDate);
  if (startMinutes == null) {
    return ymd;
  }
  const boundaryToday = localBoundaryAt(ymd, startMinutes);
  if (!boundaryToday) return ymd;
  if (referenceDate.getTime() >= boundaryToday.getTime()) {
    return ymd;
  }
  return addDaysToYMD(ymd, -1);
}

/**
 * Half-open window [start, end) in local time for the logical day anchored at `anchorYmd`.
 */
export function getGuestActivityDayWindowBounds(anchorYmd, startMinutes) {
  if (startMinutes == null) {
    const start = localBoundaryAt(anchorYmd, 0);
    if (!start) return null;
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return { start, end };
  }
  const start = localBoundaryAt(anchorYmd, startMinutes);
  if (!start) return null;
  const end = localBoundaryAt(addDaysToYMD(anchorYmd, 1), startMinutes);
  if (!end) return null;
  return { start, end };
}

/**
 * Inclusive calendar days to request from the API for the logical anchor.
 */
export function getCalendarFetchRangeForLogicalAnchor(anchorYmd, startMinutes) {
  if (startMinutes == null) {
    return { from: anchorYmd, to: anchorYmd };
  }
  return { from: anchorYmd, to: addDaysToYMD(anchorYmd, 1) };
}

export function eventStartsInGuestActivityDayWindow(
  eventStart,
  anchorYmd,
  startMinutes,
) {
  if (!(eventStart instanceof Date) || Number.isNaN(eventStart.getTime())) {
    return false;
  }
  const bounds = getGuestActivityDayWindowBounds(anchorYmd, startMinutes);
  if (!bounds) return false;
  const t = eventStart.getTime();
  return t >= bounds.start.getTime() && t < bounds.end.getTime();
}

export function filterGuestEventsByActivityDay(events, anchorYmd, startMinutes) {
  if (!Array.isArray(events)) return [];
  if (startMinutes == null) {
    return events;
  }
  return events.filter((event) => {
    const start = parseSafeDate(event.start);
    return eventStartsInGuestActivityDayWindow(start, anchorYmd, startMinutes);
  });
}
